import * as React from 'react';

import Box from '@mui/material/Box';
import type { BoxProps } from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import QIllustrationInternal from 'components/QIllustration/QIllustrationInternal';
import type { IllustrationSize } from 'components/QIllustration/QIllustrationInternal';
import ReportProblemLink from 'components/ReportProblemLink';

// Size constants
const SIZES = {
  small: {
    icon: { width: 96, height: 96 },
    spinner: 48,
    qIllustration: 'md' as IllustrationSize,
    typography: {
      title: 'bodyMedium' as const,
      subtitle: 'paragraphMedium' as const,
      description: 'paragraphRegular' as const,
      subDescription: 'captionRegular' as const,
    },
    button: 'medium' as const,
  },
  large: {
    icon: { width: 128, height: 128 },
    spinner: 64,
    qIllustration: 'lg' as IllustrationSize,
    typography: {
      title: 'displayMedium' as const,
      subtitle: 'bodyMedium' as const,
      description: 'bodyRegular' as const,
      subDescription: 'paragraphRegular' as const,
    },
    button: 'large' as const,
  },
} as const;

// Loading spinner component
interface LoadingSpinnerProps {
  size: 'small' | 'large';
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ size }) => {
  const sizeConfig = SIZES[size];
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: sizeConfig.icon.width,
        height: sizeConfig.icon.height,
      }}
    >
      <CircularProgress size={sizeConfig.spinner} />
    </Box>
  );
};

// Illustration component
interface IllustrationProps {
  qIllustration?: string;
  qIllustrationSize?: IllustrationSize;
  customIcon?: React.ReactElement<any> | string;
  size: 'small' | 'large';
  animate?: boolean;
  loading?: boolean;
}

const Illustration: React.FC<IllustrationProps> = ({
  qIllustration,
  qIllustrationSize,
  customIcon,
  size,
  animate,
  loading,
}) => {
  // Show loading spinner if loading prop is true
  if (loading) {
    return <LoadingSpinner size={size} />;
  }

  const animationStyle = animate
    ? {
        animation: 'subtleBounce 2s ease-in-out infinite',
        '@keyframes subtleBounce': {
          '0%, 100%': {
            transform: 'translateY(0)',
          },
          '50%': {
            transform: 'translateY(-8px)',
          },
        },
      }
    : {};

  const illustrationBoxProps = animate ? { sx: animationStyle } : {};

  if (qIllustration) {
    const sizeConfig = SIZES[size];
    return (
      <Box {...illustrationBoxProps}>
        <QIllustrationInternal name={qIllustration as any} size={qIllustrationSize || sizeConfig.qIllustration} />
      </Box>
    );
  }

  if (customIcon) {
    const sizeConfig = SIZES[size];
    if (React.isValidElement(customIcon)) {
      return <Box {...illustrationBoxProps}>{customIcon}</Box>;
    }
    return (
      <Box {...illustrationBoxProps}>
        <img alt='illustration' src={customIcon as string} {...sizeConfig.icon} />
      </Box>
    );
  }

  return null;
};

// Text content components
interface TextContentProps {
  content: React.ReactNode;
  size: 'small' | 'large';
  textAlign: 'left' | 'center';
}

// Generic text component factory
const createTextComponent = (
  variantKey: keyof typeof SIZES.small.typography,
  color: 'text.primary' | 'text.secondary' = 'text.primary',
) => {
  const TextComponent = React.memo<TextContentProps>(({ content, size, textAlign }) => {
    if (content != null && React.isValidElement(content)) {
      return <>{content}</>;
    }
    const sizeConfig = SIZES[size];
    return (
      <Typography variant={sizeConfig.typography[variantKey]} color={color} align={textAlign}>
        {content}
      </Typography>
    );
  });

  TextComponent.displayName = `TextComponent(${variantKey})`;
  return TextComponent;
};

// Create text components using the factory
const TitleText = createTextComponent('title', 'text.primary');
const SubtitleText = createTextComponent('subtitle', 'text.secondary');
const DescriptionText = createTextComponent('description', 'text.secondary');
const SubDescriptionText = createTextComponent('subDescription', 'text.secondary');

// Layout components
interface LayoutProps {
  // Content hierarchy
  titleProp?: React.ReactNode;
  subtitleProp?: React.ReactNode;
  descriptionProp?: React.ReactNode;
  subDescriptionProp?: React.ReactNode;

  // Icons & Illustrations
  qIllustration?: string;
  qIllustrationSize?: IllustrationSize;
  customIcon?: React.ReactElement<any> | string;

  // Layout and sizing
  size: 'small' | 'large';
  textAlign: 'left' | 'center';
  orientation: 'vertical' | 'horizontal';

  // Interactive elements
  button?: boolean | string | React.ReactElement<any>;
  buttonVariant?: 'text' | 'outlined' | 'contained' | 'tonal';
  buttonId?: string;
  onClick?: (e: React.SyntheticEvent) => void;

  // States and behaviorspnpm
  loading?: boolean;
  animate?: boolean;

  // Additional content and actions
  children?: React.ReactNode;
  report?: boolean | string;
}

// Unified layout component that handles both orientations
const ZeroStateLayout: React.FC<LayoutProps> = ({
  titleProp,
  subtitleProp,
  descriptionProp,
  subDescriptionProp,
  qIllustration,
  qIllustrationSize,
  customIcon,
  size,
  textAlign,
  orientation,
  button,
  buttonVariant,
  buttonId,
  onClick,
  loading,
  animate,
  children,
  report,
}) => {
  // Icon & Illustration elements
  const illustration = (
    <Illustration
      qIllustration={qIllustration}
      qIllustrationSize={qIllustrationSize}
      customIcon={customIcon}
      size={size}
      animate={animate}
      loading={loading}
    />
  );

  const textContent = (
    <Stack gap={1}>
      <Stack gap={0.5}>
        <TitleText content={titleProp} size={size} textAlign={textAlign} />
        <SubtitleText content={subtitleProp} size={size} textAlign={textAlign} />
      </Stack>
      <Stack gap={0.5}>
        <DescriptionText content={descriptionProp} size={size} textAlign={textAlign} />
        <SubDescriptionText content={subDescriptionProp} size={size} textAlign={textAlign} />
      </Stack>
    </Stack>
  );

  const actionArea = button ? (
    React.isValidElement(button) ? (
      button
    ) : (
      <Button variant={buttonVariant || 'contained'} onClick={onClick} size={SIZES[size].button} id={buttonId}>
        {typeof button === 'boolean' ? 'Try Again' : button}
      </Button>
    )
  ) : null;

  const reportContent = report ? (
    <ReportProblemLink feedbackSubject={typeof report === 'string' ? report : titleProp || 'report state'} />
  ) : null;

  if (orientation === 'horizontal') {
    return (
      <>
        {illustration}
        <Stack
          alignItems='flex-start'
          justifyContent='center'
          flex={1}
          sx={{ maxWidth: '900px' }}
          gap={size === 'small' ? 2 : 3}
        >
          {textContent}
          {children}
          {actionArea}
          {reportContent}
        </Stack>
      </>
    );
  }

  // Vertical layout (default)
  return (
    <>
      {illustration}
      <Stack alignItems='center' justifyContent='center' sx={{ maxWidth: '900px' }} gap={size === 'small' ? 2 : 3}>
        {textContent}
        {children}
        {actionArea}
        {reportContent}
      </Stack>
    </>
  );
};

interface ZeroStateViewProps extends Omit<BoxProps, 'children' | 'title'> {
  // Content hierarchy (most important to least important)
  /** Title text content */
  title?: React.ReactNode;
  /** Subtitle text content */
  subtitle?: React.ReactNode;
  /** Description text content */
  description?: React.ReactNode;
  /** Sub-description text content */
  subDescription?: React.ReactNode;

  // Visual elements
  /** QIllustration name to display */
  qIllustration?: string;
  /** Size of the QIllustration */
  qIllustrationSize?: IllustrationSize;
  /** Legacy icon support - can be React element or string URL */
  customIcon?: React.ReactElement<any> | string;

  // Layout and sizing
  /** Component size */
  size?: 'small' | 'large';
  /** Layout orientation - vertical (default) or horizontal */
  orientation?: 'vertical' | 'horizontal';
  /** Whether to center the component absolutely */
  center?: boolean;

  // Interactive elements
  /** Button configuration */
  button?: boolean | string | React.ReactElement<any>;
  /** Button variant */
  buttonVariant?: 'text' | 'outlined' | 'contained' | 'tonal';
  /** Button id */
  buttonId?: string;
  /** Click handler for the button */
  onClick?: (e: React.SyntheticEvent) => void;

  // States and behaviors
  /** Whether to show loading spinner instead of illustration */
  loading?: boolean;
  /** Whether to animate the illustration with a subtle bounce */
  animate?: boolean;

  // Additional content and actions
  /** Additional children content */
  children?: React.ReactNode;
  /** Whether to show report problem link */
  report?: boolean | string;
}

const ZeroStateView: React.FC<ZeroStateViewProps> = (props) => {
  const theme = useTheme();
  const {
    title: titleProp,
    description: descriptionProp,
    subtitle: subtitleProp,
    subDescription: subDescriptionProp,
    qIllustration,
    qIllustrationSize,
    customIcon,
    center,
    size = 'large',
    orientation = 'vertical',
    button,
    buttonVariant,
    buttonId,
    onClick,
    report,
    animate,
    loading,
    children,
    className,
    sx,
    id,
    ...other
  } = props;

  // Process text content
  const textAlign = orientation === 'horizontal' ? 'left' : 'center';

  // Prepare layout props
  const layoutProps: LayoutProps = {
    titleProp,
    subtitleProp,
    descriptionProp,
    subDescriptionProp,
    qIllustration,
    qIllustrationSize,
    customIcon,
    size,
    textAlign,
    orientation,
    button,
    buttonVariant,
    buttonId,
    onClick,
    loading,
    animate,
    children,
    report,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: orientation === 'horizontal' ? 'row' : 'column',
        justifyContent: 'center',
        height: '100%',
        gap: orientation === 'horizontal' ? theme.spacing(3) : theme.spacing(2),
        padding: theme.spacing(2),
        ...(orientation === 'horizontal' && {
          alignItems: 'center',
          justifyContent: 'center',
          justifySelf: 'center',
        }),
        ...(center && {
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }),
        ...sx,
      }}
      role='region'
      id={id}
      className={className}
      sharedcomponentid='ZeroStateView'
      {...other}
    >
      <ZeroStateLayout {...layoutProps} />
    </Box>
  );
};

export default ZeroStateView;
